import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Markdown from "../Markdown";
import Label from "../Label";
import { BeforeAfterSlider } from "components/_dashboard/makeovers/BeforeAfterSlider";
import { LocationGroupsCtaButton } from "components/_dashboard/locationGroups/LocationGroupsCtaButton";
import { FbPostingBetaCta, FbPostingPromoHero } from "components/__haim/DashboardMain/FacebookPostingPromoBanner";
import type { FeatureUpdate } from "../../static/updates/featureUpdates";

// Update content is raw HTML rendered through `rehypeRaw`, so it cannot call
// anything on its own. These entries are the escape hatch: a custom element in the
// content maps to a real component, which is how an update embeds something
// interactive instead of settling for a link.
//
// Keep them few and self-contained. Each one couples the updates window (and the
// popup) to a specific feature, which is worth it only when the alternative is
// sending the realtor somewhere they then have to search.
const markdownComponents = {
  // `<opengroups>label</opengroups>` — opens the location-groups manager, which is
  // a dialog and therefore has no route a link could point at.
  opengroups: (props: any) => <LocationGroupsCtaButton>{props.children}</LocationGroupsCtaButton>,
  // `<fbpostinghero></fbpostinghero>` and `<fbpostingbeta></fbpostingbeta>` — the Facebook group
  // posting launch's artwork and its beta sign-up (a dialog), the same as the home page banner's.
  fbpostinghero: () => <FbPostingPromoHero />,
  fbpostingbeta: () => <FbPostingBetaCta />,
  // `<beforeafter before="/before.png" after="/after.png"></beforeafter>`.
  beforeafter: (props: any) => {
    const before = props.before ?? props.node?.properties?.before;
    const after = props.after ?? props.node?.properties?.after;
    if (!before || !after) return null;
    return (
      <Box sx={{ my: 1.5, maxWidth: 480, borderRadius: 2, overflow: "hidden", boxShadow: 1 }}>
        <BeforeAfterSlider beforeUrl={before} afterUrl={after} paddingTop="66%" />
      </Box>
    );
  }
};

type FeatureUpdateArticleProps = {
  update: FeatureUpdate;
  /** The popup has its own header (badge and title), so it shows the content alone. */
  contentOnly?: boolean;
};

/** One update as the updates window shows it (title, areas, date and its content), or its content alone. */
export default function FeatureUpdateArticle({ update, contentOnly = false }: FeatureUpdateArticleProps) {
  const { t } = useTranslation();

  return (
    <Box component="article">
      {!contentOnly && (
        <>
          <Typography component="h3" variant="h6" sx={{ mt: 1, mb: 1 }}>
            {update.title}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1 }}>
            {update.areas?.map((area) => (
              <Label key={area} color="info">
                {t(`featureUpdates.areas.${area}`)}
              </Label>
            ))}
            <Typography variant="caption" color="text.secondary">
              {update.date}
            </Typography>
          </Box>
        </>
      )}
      <Markdown components={markdownComponents as any}>
        {/* Older entries repeated their title as the first Markdown heading. */}
        {update.content.replace(/^\s*#{1,6}\s+[^\r\n]*(?:\r?\n|$)/, "").trimStart()}
      </Markdown>
    </Box>
  );
}
